// App/AdminPortal/features/Settings/GlobalImages.js
import { Button, Select, Input } from "antd";
import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import useSWR from "swr";
import { useNavigate } from "react-router";
import {
  generateSearchParamsGetter,
  getNewSearchParamStringWithArgs,
  getSearchParams,
} from "../../utils/helpers";
import { getColumns } from "./columns";
import TableComponent from "../../components/Table/TableComponent";
import { BASE_URL } from "../../../Constants/EnvironmentVariableProvider";
import { RoutesLookup } from "../../../Routes/RoutesLookup";
import {
  GET_ALL_WELCOME_IMAGES,
  GET_ALL_IMAGE_USAGES,
} from "../../../API/paths";
import { DATAGRID_DEFAULT_PAGE_SIZE } from "../../../Constants/ApplicationConstants";

const { Search } = Input;

const GlobalImages = () => {
  // Initialize search params with defaults
  let [spObj, setSearchParams] = useSearchParams({
    page: "1",
    pageSize: DATAGRID_DEFAULT_PAGE_SIZE.toString(),
  });

  spObj.get("page") === null && spObj.set("page", "1");
  spObj.get("pageSize") === null &&
    spObj.set("pageSize", DATAGRID_DEFAULT_PAGE_SIZE.toString());

  // Extract search params from URL
  let [
,
    page,
    pageSize,
    filterName,
    statusType,
    imageUsage,
    startDate,
    endDate,
  ] = getSearchParams(spObj);

  const navigate = useNavigate();
  const getNewSearchParamString = generateSearchParamsGetter(spObj);

  // State for form controls
  const [searchState, setSearchState] = useState(filterName || "");
  const [statusFilter, setStatusFilter] = useState(
    statusType ? parseInt(statusType) : 0
  );
  const [imageUsageFilter, setImageUsageFilter] = useState(imageUsage || "all");
  const [dateRange, setDateRange] = useState([
    startDate ? new Date(startDate) : null,
    endDate ? new Date(endDate) : null,
  ]);

  // State for image usages dropdown options
  const [imageUsageOptions, setImageUsageOptions] = useState([
    { value: "all", label: "All Usages" },
  ]);

  // Fetch image usages for dropdown
  const imageUsagesApi = BASE_URL + GET_ALL_IMAGE_USAGES;
  const { data: imageUsagesData } = useSWR({
    url: imageUsagesApi,
  });

  // Update image usage options when data is loaded
  useEffect(() => {
    if (imageUsagesData?.result && imageUsagesData.result.length > 0) {
      const options = [
        { value: "all", label: "All Usages" },
        ...imageUsagesData.result.map((usage) => ({
          value: usage.GLIU_KeyID,
          label: usage.GLIU_Description,
        })),
      ];
      setImageUsageOptions(options);
    }
  }, [imageUsagesData]);

  // Format date for API
  const formatDate = (date) => {
    if (!date) return null;
    const d = new Date(date);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(
      2,
      "0"
    )}-${String(d.getDate()).padStart(2, "0")}`;
  };

  // Handle search
  const handleSearch = (value) => {
    setSearchParams((prevParams) => {
      const newParams = new URLSearchParams(prevParams);
      newParams.set("filterName", value);
      newParams.set("page", "1"); // Reset to first page
      return newParams;
    });
  };

  // Build API URL with search parameters
  const imagesApi = `${BASE_URL}${GET_ALL_WELCOME_IMAGES}${getNewSearchParamString()}`;

  // Fetch data
  const {
    data: images,
    isLoading: loading,
    mutate,
  } = useSWR({
    url: imagesApi,
  });

  // Refetch data when params change
  useEffect(() => {
    if (mutate) mutate();
  }, [
    filterName,
    statusType,
    imageUsage,
    startDate,
    endDate,
    pageSize,
    page,
    mutate,
  ]);

  // Update local state when URL params change
  useEffect(() => {
    setSearchState(filterName || "");
    setStatusFilter(statusType ? parseInt(statusType) : 0);
    setImageUsageFilter(imageUsage || "all");
    setDateRange([
      startDate ? new Date(startDate) : null,
      endDate ? new Date(endDate) : null,
    ]);
  }, [filterName, statusType, imageUsage, startDate, endDate]);

  // Handle status filter change
  const handleStatusChange = (value) => {
    setStatusFilter(value);
    // Force apply filters after state update
    setTimeout(() => {
      setSearchParams(
        getNewSearchParamStringWithArgs({
          page: 1,
          pageSize,
          filterName: searchState,
          statusType: value, // Use the new value directly
          imageUsage: imageUsageFilter,
          startDate: dateRange[0] ? formatDate(dateRange[0]) : null,
          endDate: dateRange[1] ? formatDate(dateRange[1]) : null,
        })
      );
    }, 0);
  };

  // Handle image usage filter change
  const handleImageUsageChange = (value) => {
    setImageUsageFilter(value);
    // Force apply filters after state update
    setTimeout(() => {
      setSearchParams(
        getNewSearchParamStringWithArgs({
          page: 1,
          pageSize,
          filterName: searchState,
          statusType: statusFilter,
          imageUsage: value, // Use the new value directly
          startDate: dateRange[0] ? formatDate(dateRange[0]) : null,
          endDate: dateRange[1] ? formatDate(dateRange[1]) : null,
        })
      );
    }, 0);
  };

  // Handle pagination change
  const onPaginationChange = async (newPage, newPageSize) => {
    setSearchParams(
      getNewSearchParamStringWithArgs({
        page: newPage,
        pageSize: newPageSize,
        filterName: searchState,
        statusType: statusFilter,
        imageUsage: imageUsageFilter,
        startDate: dateRange[0] ? formatDate(dateRange[0]) : null,
        endDate: dateRange[1] ? formatDate(dateRange[1]) : null,
      })
    );
  };

  // Get columns for table with necessary props
  const columns = getColumns({
    navigate,
    page,
    pageSize,
    statusType: statusFilter,
  });

  return (
    <div>
      {/* Search and Filter Section */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: "16px",
          marginBottom: "28px",
        }}
      >
        <Search
          placeholder="Search by description or ID..."
          allowClear
          enterButton
          size="large"
          onSearch={handleSearch}
          defaultValue={filterName || ""}
          style={{ flex: 1, minWidth: "300px", maxWidth: "400px" }} // Makes the search bar longer
        />
        <Select
          size="large"
          placeholder="Filter by Status"
          value={statusFilter}
          onChange={handleStatusChange}
          style={{ width: 200 }}
          options={[
            { value: 0, label: "All Status" },
            { value: 1, label: "Active" },
            { value: 2, label: "Inactive" },
          ]}
        />
        <Select
          size="large"
          placeholder="Filter by Usage"
          value={imageUsageFilter}
          onChange={handleImageUsageChange}
          style={{ width: 200 }}
          options={imageUsageOptions}
        />
        <Button
          onClick={() => {
            navigate(
              `${RoutesLookup.SETTINGS}/create?page=${
                page ?? 1
              }&pageSize=${pageSize}`
            );
          }}
          type="primary"
        >
          + Add new image
        </Button>
      </div>

      {/* Table */}
      <div>
        <TableComponent
          loading={loading}
          data={images?.result}
          totalRecords={images?.totalRecords}
          columns={columns}
          border
          rowkey="GLIM_KeyID"
          pagination={{
            totalPage: images?.totalPages * pageSize,
            pageSize: pageSize,
          }}
          onPaginationChange={onPaginationChange}
          isServerPagination
        />
      </div>
    </div>
  );
};

export default GlobalImages;